import { useEffect, useState } from 'react';

type UseIntersectionObserverParams = {
  isEnabled: boolean;
  onIntersect: () => void;
  root?: Element | Document | null;
  useParentAsRoot?: boolean;
  rootMargin?: string;
  threshold?: number | number[];
  reobserveKey?: string | number;
};

export const useIntersectionObserver = ({
  isEnabled,
  onIntersect,
  root = null,
  useParentAsRoot = true,
  rootMargin = '0px',
  threshold = 0,
  reobserveKey,
}: UseIntersectionObserverParams) => {
  const [node, setNode] = useState<Element | null>(null);
  const resolvedRoot = root ?? (useParentAsRoot ? node?.parentElement : null) ?? null;

  useEffect(() => {
    if (!isEnabled || !node) {
      return;
    }

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0]?.isIntersecting) {
          onIntersect();
        }
      },
      { root: resolvedRoot, rootMargin, threshold }
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, [
    isEnabled,
    node,
    onIntersect,
    resolvedRoot,
    rootMargin,
    threshold,
    reobserveKey,
    useParentAsRoot,
  ]);

  return {
    setObservedNode: setNode,
  };
};
